import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import logoWhite from "@/assets/logo-white.webp";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === "login" ? await login(email, password, remember) : await register(name, email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="flex h-screen w-full">
      <div className="relative hidden w-1/2 bg-[#e84e34] lg:block">
        <div className="absolute bottom-0 left-0 p-12">
          <img src={logoWhite} alt="Frdm BizOps" className="h-20 w-auto object-contain" data-testid="login-logo" />
          <h2 className="mt-6 font-heading text-4xl font-bold tracking-tight text-white">Frdm BizOps</h2>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center bg-[#e84e34] p-1.5">
              <img src={logoWhite} alt="Frdm BizOps" className="h-full w-auto object-contain" data-testid="login-logo-mobile" />
            </div>
            <span className="font-heading text-lg font-bold">Frdm BizOps</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900">
            {mode === "login" ? "Welcome Back" : "Create your account"}
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Full name</label>
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]"
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]"
                placeholder="you@frdm.coffee"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Password</label>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]"
                placeholder="••••••••"
              />
            </div>
            {mode === "login" && (
              <label className="flex items-center gap-2 text-sm text-zinc-600" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 accent-[#e84e34]"
                />
                Keep me signed in for 30 days
              </label>
            )}
            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 bg-[#e84e34] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#c73e26] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-zinc-500">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-medium text-[#e84e34] hover:underline"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
